import React, { useState, useEffect } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const STATUS_GRADIENTS = {
  Booked: "linear-gradient(135deg, #7A9E7E 0%, #4E7A52 100%)",
  Planned: "linear-gradient(135deg, #C9963A 0%, #A07820 100%)",
  "Bucket List": "linear-gradient(135deg, #8A93CC 0%, #5A6AB0 100%)",
  Past: "linear-gradient(135deg, #B0AEA9 0%, #888480 100%)",
};

const TRANSPORT_OPTIONS = ["Plane", "Driving", "Rental Car", "Ferry", "Taxi", "Uber"];
const TRANSPORT_ICONS = { Plane: "✈️", Driving: "🚗", "Rental Car": "🚙", Ferry: "⛴️", Taxi: "🚕", Uber: "📱" };
const STATUS_OPTIONS_TRIP = ["Planned", "Booked", "Bucket List", "Past"];
const RATING_OPTIONS = ["", "⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐"];

function formatDateRange(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split(" to ");
  const fmt = d => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (parts.length === 2) return `${fmt(parts[0])} – ${fmt(parts[1])}`;
  return fmt(parts[0]);
}

function StatusBadge({ status, light }) {
  const styles = {
    Booked: light ? { bg: "rgba(255,255,255,0.25)", color: "#fff" } : { bg: "#EAF4EA", color: "#3A7A3E" },
    Planned: light ? { bg: "rgba(255,255,255,0.25)", color: "#fff" } : { bg: "#FDF6E3", color: "#A07820" },
    Past: light ? { bg: "rgba(255,255,255,0.25)", color: "#fff" } : { bg: "#F2F0ED", color: "#9E9B96" },
    "Bucket List": light ? { bg: "rgba(255,255,255,0.25)", color: "#fff" } : { bg: "#EEF0FA", color: "#5A6AB0" },
  };
  const s = styles[status] || styles.Planned;
  return <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", padding: "3px 8px", borderRadius: 20, background: s.bg, color: s.color, backdropFilter: light ? "blur(4px)" : "none" }}>{status}</span>;
}

function TripCard({ trip, onTap }) {
  const gradient = STATUS_GRADIENTS[trip.status] || STATUS_GRADIENTS.Planned;
  const isPast = trip.status === "Past";
  return (
    <div onClick={() => onTap(trip)} style={{ background: "var(--white)", borderRadius: 16, marginBottom: 12, border: "1px solid var(--border)", overflow: "hidden", cursor: "pointer", opacity: isPast ? 0.75 : 1, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ height: 110, position: "relative", background: trip.coverPhoto ? `url(${trip.coverPhoto}) center/cover no-repeat` : gradient }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.5) 100%)" }} />
        <div style={{ position: "absolute", bottom: 10, left: 14, right: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>{trip.name}</div>
            {trip.location && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginTop: 2, textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>📍 {trip.location}</div>}
          </div>
          <StatusBadge status={trip.status} light />
        </div>
      </div>
      <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{trip.dates ? formatDateRange(trip.dates) : <span style={{ fontStyle: "italic" }}>No dates yet</span>}</div>
        <div style={{ display: "flex", gap: 4 }}>{trip.transport?.map(t => <span key={t} title={t} style={{ fontSize: 14 }}>{TRANSPORT_ICONS[t] || "🚌"}</span>)}</div>
      </div>
    </div>
  );
}

function CloseButton({ onClose }) {
  return (
    <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: "50%", background: "var(--cream-hover)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}>
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 1L12 12M12 1L1 12" stroke="#9E9B96" strokeWidth="1.8" strokeLinecap="round" /></svg>
    </button>
  );
}

function Sheet({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <CloseButton onClose={onClose} />
        {children}
        <div style={{ height: 32 }} />
      </div>
    </div>
  );
}

function CollapsibleSection({ icon, title, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: "var(--charcoal)", display: "flex", alignItems: "center", gap: 8 }}>{icon} {title}</span>
        <span style={{ color: "var(--muted)", fontSize: 16, display: "inline-block", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
      </button>
      {open && <div style={{ padding: "0 20px 16px" }}>{children}</div>}
    </div>
  );
}

function EditSheetShell({ title, onClose, onSave, saving, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", background: "var(--white)", borderRadius: "20px 20px 0 0", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>{title}</span>
          <button onClick={onSave} disabled={saving} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 800, color: saving ? "var(--muted)" : "var(--sage)", cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div style={{ overflowY: "auto", paddingBottom: 24 }}>{children}</div>
      </div>
    </div>
  );
}

const field = { padding: "12px 20px", borderBottom: "1px solid var(--border)" };
const label = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
const textInputStyle = { width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" };
const selStyle = { border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent", cursor: "pointer" };

function TripEditSheet({ trip, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: trip.name || "", status: trip.status || "Planned",
    dateStart: trip.dateStart || "", dateEnd: trip.dateEnd || "",
    transport: trip.transport || [], hotel: trip.hotelText || "", hotelAddress: trip.hotelAddress || "",
    tripItUrl: trip.tripItUrl || "", notes: trip.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleTransport = (t) => setForm(f => ({ ...f, transport: f.transport.includes(t) ? f.transport.filter(x => x !== t) : [...f.transport, t] }));

  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: trip.id, action: "updateTrip", value: form }),
    });
    setSaving(false);
    onSaved({ ...trip, ...form, dates: form.dateStart ? `${form.dateStart}${form.dateEnd ? " to " + form.dateEnd : ""}` : null, hotelText: form.hotel });
  };

  return (
    <EditSheetShell title="Edit Trip" onClose={onClose} onSave={handleSave} saving={saving}>
      <div style={field}><div style={label}>Trip Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>Status</div>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={selStyle}>{STATUS_OPTIONS_TRIP.map(s => <option key={s}>{s}</option>)}</select>
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>Start Date</div>
        <input type="date" value={form.dateStart} onChange={e => setForm({ ...form, dateStart: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>End Date</div>
        <input type="date" value={form.dateEnd} onChange={e => setForm({ ...form, dateEnd: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={field}>
        <div style={label}>Transportation</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
          {TRANSPORT_OPTIONS.map(t => (
            <button key={t} onClick={() => toggleTransport(t)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${form.transport.includes(t) ? "var(--sage)" : "var(--border)"}`, background: form.transport.includes(t) ? "var(--sage)" : "transparent", color: form.transport.includes(t) ? "#fff" : "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{TRANSPORT_ICONS[t]} {t}</button>
          ))}
        </div>
      </div>
      <div style={field}><div style={label}>Hotel (text)</div><input value={form.hotel} onChange={e => setForm({ ...form, hotel: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>Hotel Address</div><input value={form.hotelAddress} onChange={e => setForm({ ...form, hotelAddress: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>TripIt URL</div><input value={form.tripItUrl} onChange={e => setForm({ ...form, tripItUrl: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, borderBottom: "none" }}><div style={label}>Trip Notes</div><textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...textInputStyle, resize: "none", lineHeight: 1.5 }} /></div>
    </EditSheetShell>
  );
}

function TripDetail({ trip: initialTrip, onClose, onPlacesTap, onHotelsTap, readOnly = false }) {
  const [trip, setTrip] = useState(initialTrip);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const gradient = STATUS_GRADIENTS[trip.status] || STATUS_GRADIENTS.Planned;

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=tripDetail&pageId=${trip.id}`)
      .then(r => r.json()).then(setDetail).catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [trip.id]);

  return (
    <>
      <Sheet onClose={onClose}>
        <div style={{ height: 160, position: "relative", margin: "8px 0 0", background: trip.coverPhoto ? `url(${trip.coverPhoto}) center/cover no-repeat` : gradient }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.55) 100%)" }} />
          <div style={{ position: "absolute", bottom: 14, left: 20, right: 20 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,0.3)", marginBottom: 6 }}>{trip.name}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <StatusBadge status={trip.status} light />
              {trip.location && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>📍 {trip.location}</span>}
              {trip.dates && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{formatDateRange(trip.dates)}</span>}
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 20px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            {trip.transport?.map(t => <span key={t} title={t} style={{ fontSize: 16 }}>{TRANSPORT_ICONS[t] || "🚌"}</span>)}
          </div>
          {trip.tripItUrl && (
            <a href={trip.tripItUrl} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 10, background: "var(--white)", border: "1px solid var(--border)", textDecoration: "none", marginBottom: 10 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 14, color: "var(--charcoal)" }}><span style={{ width: 30, height: 30, borderRadius: 8, background: "var(--sage-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🗺️</span>TripIt</span>
              <span style={{ fontSize: 13, color: "var(--muted)" }}>→</span>
            </a>
          )}
          {!readOnly && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowEdit(true)} style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--sage)", color: "var(--sage)", fontWeight: 700, fontSize: 13, background: "var(--white)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
              <a href={trip.notionUrl} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--border)", color: "var(--muted)", fontWeight: 600, fontSize: 13, textDecoration: "none", background: "var(--cream-hover)" }}>Notion ↗</a>
            </div>
          )}
        </div>
        {detailLoading && <div style={{ padding: "0 20px 16px", color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>Loading details…</div>}
        {detail?.hotels?.length > 0 && (
          <CollapsibleSection icon="🏨" title="Hotels" defaultOpen>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {detail.hotels.map(h => (
                <div key={h.id} onClick={() => { onClose(); onHotelsTap?.(h.id); }} style={{ padding: "10px 12px", background: "var(--cream-hover)", borderRadius: 10, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--charcoal)" }}>{h.name}</div>
                    {h.rating && <span style={{ fontSize: 11 }}>{h.rating}</span>}
                  </div>
                  {h.address && <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>{h.address}</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    {h.address && <a href={`https://maps.google.com/?q=${encodeURIComponent(h.address)}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, fontWeight: 600, color: "var(--sage)", textDecoration: "none", padding: "5px 10px", borderRadius: 7, border: "1px solid var(--sage)" }}>Directions ↗</a>}
                    {h.website && <a href={h.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", textDecoration: "none", padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)" }}>Website ↗</a>}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
        {detail?.places?.length > 0 && (
          <CollapsibleSection icon="📍" title="Places" defaultOpen>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {detail.places.map(p => (
                <div key={p.id} onClick={() => { onClose(); onPlacesTap?.(p.id); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                  <span style={{ fontSize: 13, color: "var(--charcoal)" }}>{p.name}{p.location ? ` · ${p.location}` : ""}</span>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(p.name + (p.location ? " " + p.location : ""))}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: "var(--sage)", textDecoration: "none", fontWeight: 600 }}>Maps ↗</a>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
        {(trip.notes || trip.hotelText) && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>{trip.notes || trip.hotelText}</div>
        )}
        {detail?.blocks?.length > 0 && (
          <div style={{ margin: "0 20px 16px", padding: "12px 14px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>
            {detail.blocks.map((b, i) => <div key={i} style={{ marginBottom: i < detail.blocks.length - 1 ? 6 : 0 }}>{b.type === "bulleted_list_item" ? `• ${b.text}` : b.text}</div>)}
          </div>
        )}
        {detail?.activities?.length > 0 && (
          <CollapsibleSection icon="🎯" title="Activities">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {detail.activities.map(a => <div key={a.id} style={{ fontSize: 13, color: "var(--charcoal)" }}>• {a.name}</div>)}
            </div>
          </CollapsibleSection>
        )}
      </Sheet>
      {showEdit && <TripEditSheet trip={trip} onClose={() => setShowEdit(false)} onSaved={(updated) => { setTrip(updated); setShowEdit(false); }} />}
    </>
  );
}

function HotelEditSheet({ hotel, onClose, onSaved }) {
  const [form, setForm] = useState({ name: hotel.name || "", address: hotel.address || "", website: hotel.website || "", rating: hotel.rating || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: hotel.id, action: "updateHotel", value: form }),
    });
    setSaving(false);
    onSaved({ ...hotel, ...form });
  };

  return (
    <EditSheetShell title="Edit Hotel" onClose={onClose} onSave={handleSave} saving={saving}>
      <div style={field}><div style={label}>Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>Address</div><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>Website</div><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, borderBottom: "none", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>Rating</div>
        <select value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })} style={selStyle}>
          {RATING_OPTIONS.map(r => <option key={r} value={r}>{r || "No rating"}</option>)}
        </select>
      </div>
    </EditSheetShell>
  );
}

function HotelCard({ hotel, onUpdated, autoExpand, highlighted, readOnly = false }) {
  const [h, setH] = useState(hotel);
  const [showEdit, setShowEdit] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [blocksLoaded, setBlocksLoaded] = useState(false);

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !blocksLoaded) {
      fetch(`/.netlify/functions/notion?type=hotelDetail&pageId=${h.id}`)
        .then(r => r.json()).then(d => { setBlocks(d.blocks || []); setBlocksLoaded(true); })
        .catch(() => setBlocksLoaded(true));
    }
  };

  useEffect(() => {
    if (autoExpand) handleExpand();
  }, [autoExpand]);

  return (
    <>
      <div style={{ background: "var(--white)", borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: `1.5px solid ${highlighted ? "var(--sage)" : "var(--border)"}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div onClick={handleExpand} style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)", flex: 1, marginRight: 8, cursor: "pointer" }}>{h.name}</div>
          {!readOnly && <button onClick={() => setShowEdit(true)} style={{ background: "none", border: "none", color: "var(--sage)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>}
        </div>
        {h.rating ? <div style={{ fontSize: 14, marginBottom: 6 }}>{h.rating}</div> : <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic", marginBottom: 6 }}>No rating yet</div>}
        {h.address && <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>{h.address}</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {h.address && <a href={`https://maps.google.com/?q=${encodeURIComponent(h.address)}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "var(--sage)", textDecoration: "none", padding: "6px 12px", borderRadius: 8, border: "1px solid var(--sage)" }}>Directions ↗</a>}
          {h.website && <a href={h.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textDecoration: "none", padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border)" }}>Website ↗</a>}
          {h.notionUrl && <a href={h.notionUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textDecoration: "none", padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border)" }}>Notion ↗</a>}
          <button onClick={handleExpand} style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{expanded ? "Hide notes ⌄" : "Show notes ›"}</button>
        </div>
        {expanded && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            {!blocksLoaded ? (
              <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>Loading…</div>
            ) : blocks.length > 0 ? (
              <div style={{ fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>
                {blocks.map((b, i) => <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 6 : 0 }}>{b.type === "bulleted_list_item" ? `• ${b.text}` : b.text}</div>)}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>No notes yet.</div>
            )}
          </div>
        )}
      </div>
      {showEdit && <HotelEditSheet hotel={h} onClose={() => setShowEdit(false)} onSaved={(updated) => { setH(updated); setShowEdit(false); onUpdated?.(updated); }} />}
    </>
  );
}

export function PlacesPage({ onBack, onTabTap, highlightId, readOnly = false }) {
  const [places, setPlaces] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [editingPlace, setEditingPlace] = useState(null);
  const [placeBlocks, setPlaceBlocks] = useState({});
  const placeRefs = useRef({});
  const didHighlight = useRef(false);

  useEffect(() => {
    Promise.all([
      fetch("/.netlify/functions/notion?type=places").then(r => r.json()),
      fetch("/.netlify/functions/notion?type=hotels").then(r => r.json()),
    ]).then(([pd, hd]) => { setPlaces(pd.items || []); setHotels(hd.items || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!highlightId || loading || didHighlight.current) return;
    if (!places.some(p => p.id === highlightId)) return;
    didHighlight.current = true;
    setExpanded(highlightId);
    if (!placeBlocks[highlightId]) {
      fetch(`/.netlify/functions/notion?type=placeDetail&pageId=${highlightId}`)
        .then(r => r.json()).then(d => setPlaceBlocks(prev => ({ ...prev, [highlightId]: d.blocks || [] })))
        .catch(() => setPlaceBlocks(prev => ({ ...prev, [highlightId]: [] })));
    }
    setTimeout(() => placeRefs.current[highlightId]?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [highlightId, loading, places, placeBlocks]);

  const STATUS_OPTIONS = [{ id: "been", label: "Been There" }, { id: "want", label: "Want to Go" }];
  const toggleStatus = (id) => setStatusFilter(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  const selectAll = () => setStatusFilter(STATUS_OPTIONS.map(s => s.id));
  const deselectAll = () => setStatusFilter([]);

  const filtered = places.filter(p => {
    if (statusFilter.length === 0) return true;
    const wantsBeen = statusFilter.includes("been");
    const wantsWant = statusFilter.includes("want");
    if (wantsBeen && wantsWant) return true;
    if (wantsBeen) return p.beenThere;
    if (wantsWant) return !p.beenThere;
    return true;
  });

  const handlePlaceUpdated = (updated) => {
    setPlaces(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setEditingPlace(null);
  };

  const toggleExpand = (place) => {
    const isOpen = expanded === place.id;
    setExpanded(isOpen ? null : place.id);
    if (!isOpen && !placeBlocks[place.id]) {
      fetch(`/.netlify/functions/notion?type=placeDetail&pageId=${place.id}`)
        .then(r => r.json()).then(d => setPlaceBlocks(prev => ({ ...prev, [place.id]: d.blocks || [] })))
        .catch(() => setPlaceBlocks(prev => ({ ...prev, [place.id]: [] })));
    }
  };

  return (
    <div className="app">
      <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>📍 Places</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>All places we've been or want to go</div>
        </div>
      </header>

      <div style={{ padding: "12px 16px 4px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {STATUS_OPTIONS.map(s => {
          const checked = statusFilter.includes(s.id);
          return (
            <button key={s.id} onClick={() => toggleStatus(s.id)} style={{ padding: "8px 14px", borderRadius: 20, border: `1.5px solid ${checked ? "var(--charcoal)" : "var(--border)"}`, background: checked ? "var(--charcoal)" : "var(--white)", color: checked ? "#fff" : "var(--charcoal)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{checked ? "✓ " : ""}{s.label}</button>
          );
        })}
        <button onClick={selectAll} style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--sage)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Select all</button>
        <button onClick={deselectAll} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
      </div>

      <div className="scroll-body" style={{ padding: "12px 16px" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div> : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>No places match this filter.</div>
        ) : filtered.map(place => {
          const isOpen = expanded === place.id;
          const isHighlighted = highlightId === place.id;
          const linkedHotels = hotels.filter(h => place.hotelIds?.includes(h.id));
          const blocks = placeBlocks[place.id];
          return (
            <div key={place.id} ref={el => placeRefs.current[place.id] = el} style={{ marginBottom: 10 }}>
              <div style={{ background: "var(--white)", borderRadius: 12, border: `1.5px solid ${isHighlighted ? "var(--sage)" : "var(--border)"}` }}>
                <div onClick={() => toggleExpand(place)} style={{ padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {place.beenThere && <span title="Been there" style={{ fontSize: 14 }}>✅</span>}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>{place.name}</div>
                      {place.location && <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>{place.location}</div>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {linkedHotels.length > 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>🏨 {linkedHotels.length}</span>}
                    <span style={{ color: "var(--muted)", transform: isOpen ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform 0.2s" }}>⌄</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: "0 16px 14px", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                    {!readOnly && (
                      <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                        <button onClick={() => setEditingPlace(place)} style={{ background: "none", border: "none", color: "var(--sage)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Edit Place</button>
                        {place.notionUrl && <a href={place.notionUrl} target="_blank" rel="noreferrer" style={{ color: "var(--muted)", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>Notion ↗</a>}
                      </div>
                    )}
                    {linkedHotels.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                        {linkedHotels.map(h => (
                          <div key={h.id} style={{ background: "var(--cream-hover)", borderRadius: 10, padding: "10px 12px", border: "1px solid var(--border)" }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontWeight: 600, fontSize: 13, color: "var(--charcoal)" }}>{h.name}</span>
                              {h.rating && <span style={{ fontSize: 11 }}>{h.rating}</span>}
                            </div>
                            {h.address && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{h.address}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>Notes</div>
                    {!blocks ? (
                      <div style={{ fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>Loading…</div>
                    ) : blocks.length > 0 ? (
                      <div style={{ padding: "10px 12px", background: "var(--cream-hover)", borderRadius: 10, fontSize: 13, color: "var(--charcoal)", lineHeight: 1.6 }}>
                        {blocks.map((b, i) => <div key={i} style={{ marginBottom: i < blocks.length - 1 ? 6 : 0 }}>{b.type === "bulleted_list_item" ? `• ${b.text}` : b.text}</div>)}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>No notes yet.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
          </div>
        ))}
      </nav>
      {editingPlace && <PlaceEditSheet place={editingPlace} onClose={() => setEditingPlace(null)} onSaved={handlePlaceUpdated} />}
    </div>
  );
}

function PlaceEditSheet({ place, onClose, onSaved }) {
  const [form, setForm] = useState({ name: place.name || "", location: place.location || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: place.id, action: "updatePlace", value: form }),
    });
    setSaving(false);
    onSaved({ ...place, ...form });
  };

  return (
    <EditSheetShell title="Edit Place" onClose={onClose} onSave={handleSave} saving={saving}>
      <div style={field}><div style={label}>Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, borderBottom: "none" }}><div style={label}>Location</div><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={textInputStyle} /></div>
    </EditSheetShell>
  );
}

function PlaceFilterDropdown({ selectedPlaces, onChange, places }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const isFiltered = selectedPlaces.length > 0;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => onChange(selectedPlaces.includes(id) ? selectedPlaces.filter(p => p !== id) : [...selectedPlaces, id]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1.5px solid ${isFiltered ? "var(--charcoal)" : "var(--border)"}`, background: isFiltered ? "var(--charcoal)" : "var(--white)", color: isFiltered ? "#fff" : "var(--charcoal)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Place
        {isFiltered && <span style={{ background: "#fff", color: "var(--charcoal)", borderRadius: 10, fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>{selectedPlaces.length}</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 200, maxHeight: 280, overflowY: "auto" }}>
          {places.map((p, i) => {
            const checked = selectedPlaces.includes(p.id);
            return (
              <div key={p.id} onClick={() => toggle(p.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer", borderBottom: i < places.length - 1 ? "1px solid var(--border)" : "none", background: checked ? "var(--cream-hover)" : "transparent" }}>
                <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: "var(--charcoal)" }}>{p.name}</span>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: checked ? "none" : "2px solid var(--border)", background: checked ? "var(--sage)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {checked && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
              </div>
            );
          })}
          {selectedPlaces.length > 0 && <div onClick={() => { onChange([]); setOpen(false); }} style={{ padding: "10px 16px", fontSize: 12, color: "var(--muted)", fontWeight: 600, cursor: "pointer", textAlign: "center", borderTop: "1px solid var(--border)" }}>Clear filter</div>}
        </div>
      )}
    </div>
  );
}

export function HotelsPage({ onBack, onTabTap, highlightId, readOnly = false }) {
  const [hotels, setHotels] = useState([]);
  const [places, setPlaces] = useState([]);
  const [placeFilter, setPlaceFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const hotelRefs = useRef({});

  useEffect(() => {
    Promise.all([
      fetch("/.netlify/functions/notion?type=hotels").then(r => r.json()),
      fetch("/.netlify/functions/notion?type=places").then(r => r.json()),
    ]).then(([hd, pd]) => { setHotels(hd.items || []); setPlaces(pd.items || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!highlightId || loading) return;
    if (!hotels.some(h => h.id === highlightId)) return;
    setTimeout(() => hotelRefs.current[highlightId]?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [highlightId, loading, hotels]);

  const filtered = placeFilter.length ? hotels.filter(h => h.placeIds?.some(id => placeFilter.includes(id))) : hotels;

  return (
    <div className="app">
      <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>🏨 Hotels</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>All hotels we've stayed or considered</div>
        </div>
      </header>
      <div style={{ padding: "12px 16px 4px" }}>
        <PlaceFilterDropdown selectedPlaces={placeFilter} onChange={setPlaceFilter} places={places} />
      </div>
      <div className="scroll-body" style={{ padding: "8px 16px" }}>
        {loading ? <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div> : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>No hotels yet.</div>
        ) : filtered.map(h => (
          <div key={h.id} ref={el => hotelRefs.current[h.id] = el}>
            <HotelCard hotel={h} autoExpand={h.id === highlightId} highlighted={h.id === highlightId} readOnly={readOnly} />
          </div>
        ))}
      </div>
      <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
          </div>
        ))}
      </nav>
    </div>
  );
}

function AddTripSheet({ defaultStatus, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: "", status: defaultStatus || "Planned",
    dateStart: "", dateEnd: "",
    transport: [], hotel: "", hotelAddress: "",
    tripItUrl: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleTransport = (t) => setForm(f => ({ ...f, transport: f.transport.includes(t) ? f.transport.filter(x => x !== t) : [...f.transport, t] }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Trip name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/notion", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createTrip", value: form }),
      });
      const data = await res.json();
      if (!res.ok || !data.item) {
        setError(data.error ? (typeof data.error === "string" ? data.error : JSON.stringify(data.error)) : "Something went wrong saving this trip.");
        setSaving(false);
        return;
      }
      onAdded(data.item);
    } catch (err) {
      setError("Network error — check your connection and try again.");
      setSaving(false);
    }
  };

  return (
    <EditSheetShell title="Add Trip" onClose={onClose} onSave={handleSave} saving={saving}>
      {error && <div style={{ margin: "0 20px 4px", padding: "10px 12px", background: "#FFF0F0", borderRadius: 8, fontSize: 13, color: "var(--rose)" }}>{error}</div>}
      <div style={field}><div style={label}>Trip Name</div><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={textInputStyle} autoFocus /></div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>Status</div>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={selStyle}>{STATUS_OPTIONS_TRIP.map(s => <option key={s}>{s}</option>)}</select>
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>Start Date</div>
        <input type="date" value={form.dateStart} onChange={e => setForm({ ...form, dateStart: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={{ ...field, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={label}>End Date</div>
        <input type="date" value={form.dateEnd} onChange={e => setForm({ ...form, dateEnd: e.target.value })} style={{ ...textInputStyle, width: "auto" }} />
      </div>
      <div style={field}>
        <div style={label}>Transportation</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
          {TRANSPORT_OPTIONS.map(t => (
            <button key={t} onClick={() => toggleTransport(t)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${form.transport.includes(t) ? "var(--sage)" : "var(--border)"}`, background: form.transport.includes(t) ? "var(--sage)" : "transparent", color: form.transport.includes(t) ? "#fff" : "var(--muted)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{TRANSPORT_ICONS[t]} {t}</button>
          ))}
        </div>
      </div>
      <div style={field}><div style={label}>Hotel (text)</div><input value={form.hotel} onChange={e => setForm({ ...form, hotel: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>Hotel Address</div><input value={form.hotelAddress} onChange={e => setForm({ ...form, hotelAddress: e.target.value })} style={textInputStyle} /></div>
      <div style={field}><div style={label}>TripIt URL</div><input value={form.tripItUrl} onChange={e => setForm({ ...form, tripItUrl: e.target.value })} style={textInputStyle} /></div>
      <div style={{ ...field, borderBottom: "none" }}><div style={label}>Trip Notes</div><textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...textInputStyle, resize: "none", lineHeight: 1.5 }} /></div>
    </EditSheetShell>
  );
}

export default function TripsPage({ onBack, onTabTap, onPlacesTap, onHotelsTap, readOnly = false }) {
  const [tab, setTab] = useState("upcoming");
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showAddTrip, setShowAddTrip] = useState(false);

  const DEFAULT_STATUS_FOR_TAB = { upcoming: "Planned", past: "Past", bucketList: "Bucket List" };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/.netlify/functions/notion?type=trips&tab=${tab}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(JSON.stringify(d.error)); setTrips([]); }
        else setTrips(d.items || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [tab]);

  const handleTripAdded = (item) => {
    setShowAddTrip(false);
    if (item.status === DEFAULT_STATUS_FOR_TAB[tab] || (tab === "upcoming" && item.status === "Booked")) {
      setTrips(prev => [item, ...prev]);
    }
  };

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "2px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)", flex: 1 }}>✈️ Trips</span>
            {!readOnly && <button onClick={() => setShowAddTrip(true)} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--sage)", border: "none", fontSize: 20, color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>+</button>}
          </div>
          <div style={{ display: "flex" }}>
            {[{ id: "upcoming", label: "Upcoming" }, { id: "past", label: "Past" }, { id: "bucketList", label: "Bucket List" }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t.id ? "var(--sage)" : "transparent"}`, color: tab === t.id ? "var(--sage)" : "var(--muted)" }}>{t.label}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body" style={{ padding: "12px 16px" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <button onClick={() => onPlacesTap?.()} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1.5px solid var(--border)", background: "var(--white)", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit" }}>📍 Places</button>
            <button onClick={() => onHotelsTap?.()} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1.5px solid var(--border)", background: "var(--white)", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit" }}>🏨 Hotels</button>
          </div>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>}
          {error && <div style={{ padding: 20, color: "var(--rose)", fontSize: 13, background: "var(--cream-hover)", borderRadius: 10, marginBottom: 12 }}>Error: {error}</div>}
          {!loading && !error && trips.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--muted)", fontSize: 14 }}>No trips here yet.</div>
          )}
          {!loading && trips.map(trip => <TripCard key={trip.id} trip={trip} onTap={setSelectedTrip} />)}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            </div>
          ))}
        </nav>
      </div>
      {selectedTrip && <TripDetail trip={selectedTrip} onClose={() => setSelectedTrip(null)} onPlacesTap={onPlacesTap} onHotelsTap={onHotelsTap} readOnly={readOnly} />}
      {showAddTrip && <AddTripSheet defaultStatus={DEFAULT_STATUS_FOR_TAB[tab]} onClose={() => setShowAddTrip(false)} onAdded={handleTripAdded} />}
    </>
  );
}
